import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Mail, Send, MessageSquare } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ic = "w-full rounded-md border border-[#E4DFD3] bg-white px-3 py-2 text-sm text-[#1A2620] placeholder-[#5C6B63] focus:border-[#2C4A3B] focus:outline-none focus:ring-1 focus:ring-[#2C4A3B]";

const Contact = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: 'General feedback',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.message.trim()) { toast.error('Please write your message before sending'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/contact`, form, { withCredentials: true });
      toast.success(data.message || 'Message sent');
      if (!data.email_sent) {
        toast.message('Saved locally — our team will be in touch.', { description: 'Email delivery had a hiccup, but your message is in our queue.' });
      }
      setDone(true);
      setForm(f => ({ ...f, message: '', subject: 'General feedback' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="contact-page">
      <div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-[#1A2620] font-['Manrope']">Contact Us</h1>
        <p className="text-[#5C6B63] mt-2 text-sm">Have feedback, a bug report, or a feature idea? We'd love to hear from you.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-[#E4DFD3] rounded-lg shadow-sm p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 bg-[#2C4A3B] rounded-lg flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-[#1A2620] font-['Manrope']">Send us a message</h2>
              <p className="text-sm text-[#5C6B63]">Goes straight to <strong>support@mushroomfarmmanager.com</strong>.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-[#1A2620] mb-1">Your Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={ic} placeholder="(optional)" data-testid="contact-name" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#1A2620] mb-1">Reply-to Email</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={ic} placeholder="you@example.com" data-testid="contact-email" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1A2620] mb-1">Subject</label>
              <select value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} className={ic} data-testid="contact-subject">
                <option>General feedback</option>
                <option>Bug report</option>
                <option>Feature request</option>
                <option>Account / billing</option>
                <option>Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#1A2620] mb-1">Message <span className="text-[#D05A4A]">*</span></label>
              <textarea
                value={form.message}
                onChange={e => setForm({ ...form, message: e.target.value })}
                rows={7}
                required
                placeholder="Tell us what's on your mind…"
                className={`${ic} resize-y min-h-[160px]`}
                data-testid="contact-message"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
              <p className="text-xs text-[#5C6B63]">We typically reply within 1–2 business days.</p>
              <button type="submit" disabled={submitting} className="bg-[#2C4A3B] hover:bg-[#3E6652] text-white rounded-md px-5 py-2.5 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="contact-submit">
                <Send className="w-4 h-4" />
                {submitting ? 'Sending…' : 'Send Message'}
              </button>
            </div>

            {done && (
              <div className="p-3 bg-[#EDF5EE] border border-[#3A7D44]/30 rounded text-sm text-[#3A7D44]" data-testid="contact-done">
                Thanks! Your message has been received. We'll get back to you soon.
              </div>
            )}
          </form>
        </div>

        <div className="bg-white border border-[#E4DFD3] rounded-lg shadow-sm p-5">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-10 h-10 bg-[#EDF5EE] rounded-lg flex items-center justify-center flex-shrink-0">
              <Mail className="w-5 h-5 text-[#3A7D44]" />
            </div>
            <div>
              <h3 className="text-base font-bold text-[#1A2620] font-['Manrope']">Reach us directly</h3>
              <p className="text-xs text-[#5C6B63]">support@mushroomfarmmanager.com</p>
            </div>
          </div>
          <ul className="text-sm text-[#1A2620] space-y-2 mt-4">
            <li>• Bug reports — include steps to reproduce + screenshots when you can</li>
            <li>• Feature requests — tell us the problem you're trying to solve</li>
            <li>• Account help — mention your email + workspace owner</li>
          </ul>
          <p className="text-xs text-[#5C6B63] mt-4 pt-4 border-t border-[#E4DFD3]">Built with care by the Mushroom Farm Manager team. Your feedback shapes the roadmap.</p>
        </div>
      </div>
    </div>
  );
};

export default Contact;
